/** 画布表格行列拖拽预览：从 TableGrid DOM 克隆行/列单元格内容 */

export type SourceContentHidden = { el: HTMLElement; prevVisibility: string };

export function getCanvasTableDom(container: HTMLElement | null): HTMLTableElement | null {
  return container?.querySelector('table') ?? null;
}

function cloneCanvasCellForDragGhost(sourceCell: HTMLTableCellElement): HTMLTableCellElement {
  const clone = sourceCell.cloneNode(true) as HTMLTableCellElement;
  clone.querySelectorAll('[contenteditable]').forEach((el) => {
    el.setAttribute('contenteditable', 'false');
  });
  clone.querySelectorAll('input, textarea').forEach((el) => el.remove());
  clone.style.pointerEvents = 'none';
  clone.style.userSelect = 'none';
  clone.style.boxSizing = 'border-box';
  return clone;
}

export function buildRowDragGhostTable(sourceTr: HTMLTableRowElement): HTMLTableElement {
  const cloneTr = document.createElement('tr');
  sourceTr.querySelectorAll<HTMLTableCellElement>(':scope > td').forEach((c) => {
    cloneTr.appendChild(cloneCanvasCellForDragGhost(c));
  });
  const tbl = document.createElement('table');
  tbl.className = 'border-collapse';
  tbl.style.width = '100%';
  tbl.style.height = '100%';
  tbl.style.tableLayout = 'fixed';
  tbl.style.borderCollapse = 'collapse';
  const tb = document.createElement('tbody');
  tb.appendChild(cloneTr);
  tbl.appendChild(tb);
  return tbl;
}

export function buildColDragGhostTable(tableDom: HTMLTableElement, colIndex: number): HTMLTableElement {
  const tbl = document.createElement('table');
  tbl.className = 'border-collapse';
  tbl.style.width = '100%';
  tbl.style.height = '100%';
  tbl.style.tableLayout = 'fixed';
  tbl.style.borderCollapse = 'collapse';
  const tb = document.createElement('tbody');
  for (let i = 0; i < tableDom.rows.length; i++) {
    const src = tableDom.rows[i].cells[colIndex] as HTMLTableCellElement | undefined;
    if (!src) continue;
    const tr = document.createElement('tr');
    tr.appendChild(cloneCanvasCellForDragGhost(src));
    tb.appendChild(tr);
  }
  tbl.appendChild(tb);
  return tbl;
}

/** 拖拽时隐藏源行单元格（保留占位） */
export function hideCanvasTableRowContents(tr: HTMLTableRowElement): SourceContentHidden[] {
  const rest: SourceContentHidden[] = [];
  tr.querySelectorAll<HTMLTableCellElement>(':scope > td').forEach((cell) => {
    rest.push({ el: cell, prevVisibility: cell.style.visibility });
    cell.style.visibility = 'hidden';
  });
  return rest;
}

/** 拖拽时隐藏源列单元格 */
export function hideCanvasTableColumnContents(
  tableDom: HTMLTableElement,
  colIndex: number,
): SourceContentHidden[] {
  const rest: SourceContentHidden[] = [];
  for (let i = 0; i < tableDom.rows.length; i++) {
    const cell = tableDom.rows[i].cells[colIndex];
    if (!cell) continue;
    rest.push({ el: cell, prevVisibility: cell.style.visibility });
    cell.style.visibility = 'hidden';
  }
  return rest;
}

export function restoreSourceContentHidden(rest: SourceContentHidden[] | undefined) {
  rest?.forEach(({ el, prevVisibility }) => {
    el.style.visibility = prevVisibility;
  });
}

export function resetBodyDragChromeStyles() {
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
}

/** 表 DOM 屏幕尺寸 ÷ 画布逻辑尺寸，含工作区 zoom 与元素 transform */
export function getTableScreenScale(
  tableDom: HTMLTableElement | null,
  layoutWidth: number,
  layoutHeight: number,
): { scaleX: number; scaleY: number } {
  if (!tableDom || layoutWidth <= 0 || layoutHeight <= 0) {
    return { scaleX: 1, scaleY: 1 };
  }
  const rect = tableDom.getBoundingClientRect();
  return {
    scaleX: rect.width / layoutWidth,
    scaleY: rect.height / layoutHeight,
  };
}
